// The wire format between the publisher and the app, and the trust boundary
// between them.
//
// Every Appwrite response is untrusted input, unlike the committed JSON in
// content/ whose shape `bun run validate` enforces. The difference is the whole
// reason this file checks rather than casts: a cast asserts a shape nobody
// verified, and here the writer is a network peer.
//
// Key order inside `data` is preserved exactly as received, because
// contentVersionOf stringifies those objects and therefore depends on it. A
// recursively key-sorting serializer would silently change every learner's
// contentVersion; the version-parity test in bundle.test.ts pins it.

import type { CourseData } from "./course.ts";
import type { Locale } from "./types.ts";

export const BUNDLE_SCHEMA_VERSION = 1;

/**
 * The publisher-side invariant, deliberately far below the bucket's 20 MB
 * ceiling. The bucket limit is a blast-radius guard against a runaway upload;
 * this is the real one, and the client enforces it on the way in so a
 * misconfigured publisher cannot make every learner download a huge artifact.
 */
export const MAX_JSON_BYTES = 1_000_000;

export class ContentFormatError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "ContentFormatError";
	}
}

export interface CourseBundle {
	readonly schemaVersion: number;
	readonly contentVersion: string;
	readonly data: CourseData;
	readonly locales: Readonly<Record<string, Locale>>;
}

interface AudioAssetEntry {
	readonly fileId: string;
	readonly sha256: string;
	readonly size: number;
	readonly mimeType: string;
}

export interface AudioManifest {
	readonly schemaVersion: number;
	readonly assets: Readonly<Record<string, AudioAssetEntry>>;
}

const HEX64 = /^[0-9a-f]{64}$/;
/** Audio only: a manifest entry that names a course or manifest file is a real
    operator error, and a generic pattern lets it through to surface later as a
    byte mismatch rather than as the naming mistake it is. */
const AUDIO_FILE_ID = /^m_[0-9a-f]{32}$/;

const isRecord = (v: unknown): v is Record<string, unknown> =>
	typeof v === "object" && v !== null && !Array.isArray(v);

const isString = (v: unknown): v is string => typeof v === "string";
const isNumber = (v: unknown): v is number => typeof v === "number" && Number.isInteger(v);
const isNonNegativeInteger = (v: unknown): v is number => isNumber(v) && v >= 0;

// A declaration, not an arrow: only a declared function's `never` narrows its callers.
function fail(what: string): never {
	throw new ContentFormatError(`Malformed content: ${what}`);
}

const json = (raw: string, what: string): Record<string, unknown> => {
	// Byte length, not string length: the limit is about what crosses the wire.
	if (new TextEncoder().encode(raw).length > MAX_JSON_BYTES)
		fail(`${what} exceeds ${MAX_JSON_BYTES} bytes`);
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return fail(`${what} is not valid JSON`);
	}
	if (!isRecord(parsed)) fail(`${what} is not a JSON object`);
	return parsed;
};

const schemaVersion = (o: Record<string, unknown>, what: string): number => {
	// Exact equality, not a range and not a coercion: a build must either
	// understand every field in the artifact or refuse it outright.
	if (o.schemaVersion !== BUNDLE_SCHEMA_VERSION) fail(`${what} has unsupported schemaVersion`);
	return BUNDLE_SCHEMA_VERSION;
};

const requireString = (v: unknown, what: string): string => {
	if (!isString(v)) fail(`${what} is not a string`);
	return v;
};

const requireNonEmptyString = (v: unknown, what: string): string => {
	const s = requireString(v, what);
	if (s === "") fail(`${what} is empty`);
	return s;
};

const requireDigest = (v: unknown, what: string): string => {
	if (!isString(v) || !HEX64.test(v)) fail(`${what} is not a 64-character lowercase sha256`);
	return v;
};

const requireSize = (v: unknown, what: string): number => {
	if (!isNonNegativeInteger(v)) fail(`${what} is not a non-negative integer`);
	return v;
};

const requireRecord = (v: unknown, what: string): Record<string, unknown> => {
	if (!isRecord(v)) fail(`${what} is not an object`);
	return v;
};

const item = (v: unknown, i: number): void => {
	const obj = requireRecord(v, `items[${i}]`);
	if (!isString(obj.id)) fail(`items[${i}] has no string id`);
};

function parseData(v: unknown): CourseData {
	const obj = requireRecord(v, "data");
	const items = requireArray(obj.items, "data.items");
	items.forEach(item);
	const units = requireArray(obj.units, "data.units");
	units.forEach((u: unknown, i: number) => {
		const unit = requireRecord(u, `data.units[${i}]`);
		if (!isString(unit.id)) fail(`data.units[${i}] has no string id`);
	});
	const confusions = obj.confusions;
	if (
		!Array.isArray(confusions) ||
		!confusions.every((c): c is string[] => Array.isArray(c) && c.every(isString))
	) {
		fail("data.confusions is not an array of string arrays");
	}
	const recipes = requireRecord(obj.recipes, "data.recipes");
	for (const [id, r] of Object.entries(recipes)) {
		if (!isRecord(r)) fail(`data.recipes["${id}"] is not an object`);
	}
	// The cast is the one this file earns: every field above is checked, and the
	// item/unit/recipe *sub-shapes* stay the committed content's contract, which
	// `bun run validate` and the version-parity test hold in place.
	return { items, units, confusions, recipes } as unknown as CourseData;
}

const requireArray = <T>(v: unknown, what: string): T[] => {
	if (!Array.isArray(v)) fail(`${what} is not an array`);
	// An array; its elements are checked by the caller, item by item.
	return v as T[];
};

export function parseCourseBundle(raw: string): CourseBundle {
	const o = json(raw, "course bundle");
	const v = schemaVersion(o, "course bundle");
	const contentVersion = requireNonEmptyString(o.contentVersion, "course bundle contentVersion");
	const locales = requireRecord(o.locales, "course bundle locales");
	for (const [lang, l] of Object.entries(locales)) {
		const loc = requireRecord(l, `locales["${lang}"]`);
		for (const [k, s] of Object.entries(loc)) {
			if (!isString(s)) fail(`locales["${lang}"]["${k}"] is not a string`);
		}
	}
	return {
		schemaVersion: v,
		contentVersion,
		data: parseData(o.data),
		// Every value of every locale was checked a string just above.
		locales: locales as Record<string, Locale>,
	};
}

export function parseAudioManifest(raw: string): AudioManifest {
	const o = json(raw, "audio manifest");
	const v = schemaVersion(o, "audio manifest");
	const assets = requireRecord(o.assets, "audio manifest assets");
	const out: Record<string, AudioAssetEntry> = {};
	for (const [name, e] of Object.entries(assets)) {
		const entry = requireRecord(e, `assets["${name}"]`);
		const fileId = requireString(entry.fileId, `assets["${name}"].fileId`);
		if (!AUDIO_FILE_ID.test(fileId)) fail(`assets["${name}"].fileId is not a valid audio file id`);
		const sha256 = requireDigest(entry.sha256, `assets["${name}"].sha256`);
		// The id is the first 32 characters of the digest. Checking the coupling
		// here means a manifest cannot point at one file while claiming another's
		// digest, so the two can never disagree about which bytes are which.
		if (fileId !== `m_${sha256.slice(0, 32)}`)
			fail(`assets["${name}"].fileId does not match its own sha256`);
		const sz = requireSize(entry.size, `assets["${name}"].size`);
		const mime = requireString(entry.mimeType, `assets["${name}"].mimeType`);
		out[name] = {
			fileId,
			sha256,
			size: sz,
			mimeType: mime,
		};
	}
	return { schemaVersion: v, assets: out };
}

const toHex = (buf: ArrayBuffer): string =>
	[...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");

/** Plain SHA-256 over arbitrary bytes. A course bundle, an audio manifest and an
    MP3 are all just bytes, so one primitive serves all three — the two named
    digests below are aliases, kept because a call site reading
    `contentBundleDigest(...)` states an intent that `sha256(...)` does not. */
export const sha256 = (bytes: Uint8Array): Promise<string> =>
	crypto.subtle.digest("SHA-256", bytes as unknown as ArrayBuffer).then(toHex);

export const contentBundleDigest = sha256;
export const audioManifestDigest = sha256;

export const courseFileId = (d: string): string => `c_${d.slice(0, 32)}`;
export const manifestFileId = (d: string): string => `a_${d.slice(0, 32)}`;
export const audioFileId = (d: string): string => `m_${d.slice(0, 32)}`;
